import React, { FC, PropsWithChildren } from "react";

import { t } from "i18next";
import styled from "styled-components";
import IconButton from "@mui/material/IconButton";

import { CheckCircleIcon, Cross as CrossIcon, DangerCircleIcon, WarningIcon } from "@/assets/icons";

export type InfoBoxType = "info" | "warning" | "error" | "success";
export type InfoBoxVariant = "outlined" | "contained";

type InfoBoxProps = {
    type?: InfoBoxType;
    variant?: InfoBoxVariant;
    onClose?: () => void;
    [x: string]: any;
};

const getDefaultColor = (type: InfoBoxType) => {
    switch (type) {
        case "warning":
            return `var(--color-warning)`;
        case "error":
            return `var(--color-error)`;
    }
    return `var(--color-success)`;
};

const getBackgroundDefaultColor = (type: InfoBoxType) => {
    switch (type) {
        case "error":
            return `var(--color-error-light);`;
        case "warning":
            return `var(--color-warning-light);`;
    }
    return `var(--color-success-light)`;
};

const getDefaultIcon = (type: InfoBoxType) => {
    switch (type) {
        case "error":
            return <DangerCircleIcon />;
        case "warning":
            return <WarningIcon />;
    }
    return <CheckCircleIcon />;
};

const StyledInfoBox = styled.div<{ $boxType: InfoBoxType; $variant: InfoBoxVariant }>`
    display: flex;
    align-items: center;
    padding: 14px 16px;
    margin: 10px 0;
    font-size: var(--size-14);
    border-radius: var(--radius-5);
    color: ${(props) => getDefaultColor(props.$boxType)};

    svg {
        width: 17px;
        height: 17px;
        fill: ${(props) => getDefaultColor(props.$boxType)};

        path {
            fill: ${(props) => getDefaultColor(props.$boxType)};
        }
    }

    p {
        flex: 1;
        padding: 0 10px;
        margin: 0;
    }

    ${(props) =>
        props.$variant === "outlined" &&
        `
            font-weight: var(--weight-600);
            border: 1px solid ${getDefaultColor(props.$boxType)};
    `}

    ${(props) =>
        props.$variant === "contained" &&
        `
        background-color: ${getBackgroundDefaultColor(props.$boxType)};
    `}
`;

const InfoBox: FC<PropsWithChildren<InfoBoxProps>> = ({
    type = "info",
    variant = "contained",
    onClose,
    children,
    ...props
}) => {
    return (
        <StyledInfoBox className="infoBox" $boxType={type} $variant={variant} {...props} role="alert">
            {getDefaultIcon(type)}
            <p>{children}</p>
            {onClose && (
                <IconButton onClick={onClose} aria-label={t("Common.Close")}>
                    <CrossIcon />
                </IconButton>
            )}
        </StyledInfoBox>
    );
};

export default InfoBox;
